import type { Company, Contact, Lead, Membership, PartyChannel } from "../../lib/types";
import {
  channelsInCompanyHistory,
  companyMatchesSearch,
  contactsInCompanyHistory,
  leadsInCompanyHistory,
  legacyCompaniesFromLeads,
  normalizeIdentitySearch,
} from "./queries";

/**
 * Modelo puro da lista de Empresas.
 *
 * A identidade empresarial já tem comandos, repositório e capabilities
 * (AGENTS §27.2); o que faltava era a superfície V2. Este módulo faz só o
 * recorte: monta linhas a partir do que o store já carregou, aplica visão,
 * busca e ordenação, e resume a seleção. Nenhuma regra de identidade nova nasce
 * aqui — merge, reclassificação e fila de candidatos continuam nos comandos.
 *
 * Duas naturezas de linha convivem de propósito:
 *
 * - **canônica**: existe como Company persistida e aceita comandos;
 * - **legada**: derivada do texto `lead.company` de um lead sem vínculo, apenas
 *   para que a lista não esconda empresas que ainda não foram promovidas.
 *
 * Uma linha legada nunca finge ser canônica: ela não recebe contatos, canais
 * nem mesclagem, e a interface diz por quê.
 */

export type CompanyViewId = "todas" | "negociando" | "sem-contato" | "mescladas";

export interface CompanyView {
  id: CompanyViewId;
  label: string;
  /** Explicação curta da regra da visão, exibida no menu lateral. */
  hint: string;
}

export const COMPANY_VIEWS: CompanyView[] = [
  { id: "todas", label: "Todas", hint: "Empresas ativas e o legado ainda não promovido." },
  {
    id: "negociando",
    label: "Em negociação",
    hint: "Empresas com ao menos uma oportunidade ativa em algum funil.",
  },
  {
    id: "sem-contato",
    label: "Sem contato",
    hint: "Empresas ativas que ainda não têm nenhuma pessoa cadastrada.",
  },
  {
    id: "mescladas",
    label: "Mescladas",
    hint: "Identidades que viraram alias de outra empresa e são somente leitura.",
  },
];

export interface CompanyRow {
  company: Company;
  /** Nome de exibição: fantasia quando existe, senão a razão social. */
  name: string;
  legalName: string;
  segment: string;
  location: string;
  contacts: Contact[];
  channels: PartyChannel[];
  leads: Lead[];
  /** Participações por desfecho, contadas sobre o histórico da empresa. */
  active: number;
  won: number;
  lost: number;
  /** Valor potencial somado das oportunidades ainda em negociação. */
  value: number;
  /** Linha persistida como Company; falso quando vem do texto de um lead. */
  canonical: boolean;
  /** Identidade mesclada: histórico preservado, escrita bloqueada. */
  tombstone: boolean;
}

export interface CompanyRowsInput {
  companies: Company[];
  contacts: Contact[];
  partyChannels: PartyChannel[];
  leads: Lead[];
  memberships: Membership[];
}

/**
 * Monta a lista completa, incluindo tombstones.
 *
 * `visibleCompanies` esconde as mescladas porque a busca global não deve
 * oferecê-las; aqui elas aparecem, porque a tela de identidade é justamente
 * onde alguém precisa enxergar o que foi consolidado.
 */
export function buildCompanyRows(input: CompanyRowsInput): CompanyRow[] {
  const { companies, contacts, partyChannels, leads, memberships } = input;
  const canonicalNames = new Set(
    companies
      .flatMap((company) => [company.legalName, company.tradeName])
      .filter((name): name is string => Boolean(name))
      .map((name) => normalizeIdentitySearch(name)),
  );
  const legacy = legacyCompaniesFromLeads(leads).filter(
    (company) => !canonicalNames.has(normalizeIdentitySearch(company.legalName)),
  );

  const rows: CompanyRow[] = [];
  for (const company of [...companies, ...legacy]) {
    const canonical = companies.some((item) => item.id === company.id);
    const companyLeads = leadsInCompanyHistory(companies, leads, company);
    const companyMemberships = memberships.filter((membership) =>
      companyLeads.some((lead) => lead.id === membership.leadId),
    );
    const active = companyMemberships.filter((membership) => membership.status === "active");
    rows.push({
      company,
      name: company.tradeName || company.legalName,
      legalName: company.legalName,
      segment: company.segment ?? "",
      location: [company.city, company.state].filter(Boolean).join(" / "),
      contacts: canonical ? contactsInCompanyHistory(companies, contacts, company.id) : [],
      channels: canonical ? channelsInCompanyHistory(companies, partyChannels, company.id) : [],
      leads: companyLeads,
      active: active.length,
      won: companyMemberships.filter((membership) => membership.status === "won").length,
      lost: companyMemberships.filter((membership) => membership.status === "lost").length,
      value: active.reduce((total, membership) => {
        const lead = companyLeads.find((item) => item.id === membership.leadId);
        return total + (lead?.potentialValue ?? 0);
      }, 0),
      canonical,
      tombstone: company.status !== "active",
    });
  }
  return rows;
}

export function matchesCompanyView(row: CompanyRow, view: CompanyViewId): boolean {
  if (view === "mescladas") return row.tombstone;
  if (row.tombstone) return false;
  if (view === "negociando") return row.active > 0;
  if (view === "sem-contato") return row.canonical && row.contacts.length === 0;
  return true;
}

export interface CompanySearchInput {
  rows: CompanyRow[];
  /** Companies canônicas, necessárias para a busca alcançar o histórico. */
  companies: Company[];
  query: string;
  /** Segmento exato ou `todos`. */
  segment: string;
}

/**
 * Busca e segmento, sem a visão.
 *
 * A separação existe para que o contador de cada visão e a tabela venham da
 * mesma seleção: o menu conta `matchesCompanyView` sobre este resultado, e a
 * tabela mostra exatamente as linhas contadas (AGENTS §10).
 */
export function searchCompanyRows(input: CompanySearchInput): CompanyRow[] {
  const { rows, companies, query, segment } = input;
  return rows.filter((row) => {
    if (segment && segment !== "todos" && row.segment !== segment) return false;
    if (query.trim() && !companyMatchesSearch(row.company, query, companies)) return false;
    return true;
  });
}

export interface CompanyFilterInput extends CompanySearchInput {
  view: CompanyViewId;
}

export function filterCompanyRows(input: CompanyFilterInput): CompanyRow[] {
  return searchCompanyRows(input).filter((row) => matchesCompanyView(row, input.view));
}

export type CompanySortKey = "empresa" | "segmento" | "contatos" | "canais" | "oportunidades" | "valor";
export type SortDirection = "asc" | "desc";

/** Ordena de forma estável: empate sempre cai no nome, em ordem alfabética. */
export function sortCompanyRows(
  rows: CompanyRow[],
  key: CompanySortKey,
  direction: SortDirection,
): CompanyRow[] {
  const factor = direction === "asc" ? 1 : -1;
  const value = (row: CompanyRow): number | string => {
    switch (key) {
      case "segmento":
        return row.segment;
      case "contatos":
        return row.contacts.length;
      case "canais":
        return row.channels.length;
      case "oportunidades":
        return row.leads.length;
      case "valor":
        return row.value;
      default:
        return row.name;
    }
  };
  return [...rows].sort((a, b) => {
    const left = value(a);
    const right = value(b);
    const comparison =
      typeof left === "number" && typeof right === "number"
        ? left - right
        : String(left).localeCompare(String(right), "pt-BR");
    if (comparison !== 0) return comparison * factor;
    return a.name.localeCompare(b.name, "pt-BR");
  });
}

export interface CompanySummary {
  companies: number;
  negotiating: number;
  contacts: number;
  value: number;
}

export function summarizeCompanies(rows: CompanyRow[]): CompanySummary {
  return {
    companies: rows.length,
    negotiating: rows.filter((row) => row.active > 0).length,
    contacts: rows.reduce((total, row) => total + row.contacts.length, 0),
    value: rows.reduce((total, row) => total + row.value, 0),
  };
}

/** Segmentos presentes na base, em ordem alfabética, para o filtro. */
export function companySegments(rows: CompanyRow[]): string[] {
  const segments = new Set(rows.map((row) => row.segment).filter(Boolean));
  return [...segments].sort((a, b) => a.localeCompare(b, "pt-BR"));
}
